import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { ATECO_CODES } from "@/data/atecoCodes";

/**
 * Searchable combobox for ATECO codes.
 * Allows selecting by code or by searching the activity description.
 *
 * Props:
 *  - value: currently selected ATECO code string (e.g. "62.01.00")
 *  - onChange: (code) => void
 *  - label: optional, also handled outside
 *  - placeholder
 *  - testid
 *  - allowClear: show a clear (X) button when a value is selected
 */
export default function AtecoCombobox({
  value,
  onChange,
  placeholder = "Cerca per codice o attività...",
  testid,
  allowClear = true,
}) {
  const [open, setOpen] = useState(false);

  const selected = useMemo(
    () => ATECO_CODES.find((c) => c.code === value),
    [value]
  );

  return (
    <div className="relative">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            data-testid={testid}
            className={cn(
              "mt-1.5 h-10 w-full justify-between rounded-lg border-slate-200 bg-white px-3 font-normal hover:bg-white focus-visible:ring-viva-500/20 focus-visible:border-viva-500",
              !selected && "text-slate-400"
            )}
          >
            <span className="truncate text-left text-sm">
              {selected ? (
                <>
                  <span className="font-medium text-slate-900">{selected.code}</span>
                  <span className="text-slate-500"> — {selected.description}</span>
                </>
              ) : value ? (
                <>
                  <span className="font-medium text-slate-900">{value}</span>
                  <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-800">
                    ATECO precedente — da rimappare
                  </span>
                </>
              ) : (
                placeholder
              )}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[min(640px,calc(100vw-3rem))] p-0"
          align="start"
          sideOffset={4}
        >
          <Command
            filter={(itemValue, search) => {
              // itemValue is "code description"; match if every whitespace-separated
              // token in the search appears in the item value (case-insensitive).
              if (!search) return 1;
              const haystack = itemValue.toLowerCase();
              const tokens = search.toLowerCase().split(/\s+/).filter(Boolean);
              return tokens.every((t) => haystack.includes(t)) ? 1 : 0;
            }}
          >
            <CommandInput
              placeholder="Cerca codice o descrizione attività..."
              data-testid={testid ? `${testid}-search` : undefined}
            />
            <CommandList className="max-h-80">
              <CommandEmpty>Nessun codice ATECO trovato.</CommandEmpty>
              <CommandGroup>
                {ATECO_CODES.map((c) => (
                  <CommandItem
                    key={c.code}
                    value={`${c.code} ${c.description}`}
                    onSelect={() => {
                      onChange(c.code);
                      setOpen(false);
                    }}
                    data-testid={testid ? `${testid}-option-${c.code}` : undefined}
                    className="flex items-start gap-2 py-2"
                  >
                    <Check
                      className={cn(
                        "mt-0.5 h-4 w-4 shrink-0",
                        value === c.code ? "opacity-100 text-viva-600" : "opacity-0"
                      )}
                    />
                    <div className="flex flex-col">
                      <span className="font-mono text-xs font-semibold text-slate-900">
                        {c.code}
                      </span>
                      <span className="text-xs text-slate-600 leading-snug">
                        {c.description}
                      </span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {allowClear && value ? (
        <button
          type="button"
          onClick={() => onChange("")}
          aria-label="Rimuovi codice ATECO"
          data-testid={testid ? `${testid}-clear` : undefined}
          className="absolute right-9 top-1/2 -translate-y-1/2 mt-[3px] text-slate-400 hover:text-slate-700"
        >
          <X className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  );
}
